import { WebSocketState } from '../typings'
import { Subscribe } from './subscribe'
import logger from './logger'

/**
 * 鲁棒ws的配置参数
 */
export type RobustWebSocketConstructorOptions = {
  url: string | ((ws: RobustWebSocket) => string)
  protocols?: string[]
  robustOptions?: RobustOptions
}

/**
 * ws 鲁棒性的配置
 */
export type RobustOptions = {
  // 等待超时时间
  timeout?: number
  // 是否进行重连的判断
  shouldReconnect?: (event: CloseEvent, ws: RobustWebSocket) => undefined | number
  // 是否1000的正常关闭code 也需要重连
  should1000Reconnect?: boolean
  // 是否监听 navigator 上的online/offline 事件
  ignoreConnectivityEvents?: boolean
  // 是否自动连接
  automaticOpen?: boolean
}

/**
 * 重连的事件详情
 */
export type RobustWebSocketEventDetail = {
  reconnects: number // 重连次数
  attempts: number // 重连尝试次数
}

export type RobustWebSocketEvent<T> = Event & {
  detail: T
}

// RobustWebSocket事件映射
export type RobustWebSocketEventMap = {
  connecting: (event: RobustWebSocketEvent<RobustWebSocketEventDetail>) => void
  timeout: (event: RobustWebSocketEvent<RobustWebSocketEventDetail>) => void
  open: (event: RobustWebSocketEvent<RobustWebSocketEventDetail>) => void
  closing: () => void
  close: (event: CloseEvent) => void
  error: (event: Event) => void
  message: (event: MessageEvent<string | ArrayBuffer | Blob | ArrayBufferView>) => void
}

/**
 * 具有鲁棒性的WebSocket封装
 * 1、自动重连
 * 2、可根据网络情况进行重连
 */
export class RobustWebSocket extends Subscribe<RobustWebSocketEventMap> {
  private _realWebSocket: WebSocket | undefined
  private _url?: string | ((ws: RobustWebSocket) => string)
  private _protocols: string[] | undefined
  private _readyState: WebSocketState = WebSocketState.NOTINITIALIZED
  private _options: RobustOptions = {
    timeout: 10000,
    shouldReconnect: (event, ws) => {
      if (event.code === 1008 || event.code === 1011) return
      return [0, 1000, 2000][ws.attempts % 3]
    },
    should1000Reconnect: false,
    ignoreConnectivityEvents: false,
    automaticOpen: false,
  }
  private _connectTimeout: any | undefined
  private _attempts = 0
  private _reconnects = -1
  private _reconnectWhenOnlineAgain = false
  public _explicitlyClosed = false
  private _pendingReconnect: NodeJS.Timeout | number | undefined
  private _connectivityEventsAttached = false
  private _heathCheckTimer: number | undefined

  public get attempts(): number {
    return this._attempts
  }

  constructor(options?: RobustWebSocketConstructorOptions) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    super()
    if (options) {
      this.setOptions(options)
    }
    this._startHealthCheck()
  }

  public setOptions(options: RobustWebSocketConstructorOptions): void {
    if (!options) return
    this._url = options.url
    this._protocols = options.protocols
    const opts = Object.assign(this._options, options.robustOptions || {})
    if (typeof opts?.timeout !== 'number') {
      throw new Error('timeout must be the number of milliseconds to timeout a connection attempt')
    }

    if (typeof opts?.shouldReconnect !== 'function') {
      throw new Error(
        'shouldReconnect must be a function that returns the number of milliseconds to wait for a reconnect attempt, or null or undefined to not reconnect.',
      )
    }
    this._options = opts
    if (this._options.automaticOpen) {
      this._createWebSocket()
    }
  }

  /**
   * 做一个健康检查
   */
  private _healthCheck = async () => {
    if (this._realWebSocket) {
      const currentState = this._realWebSocket.readyState as WebSocketState
      if (currentState !== this._readyState) {
        this._readyState = currentState
        // 太惨了，断网的时候，ws不会直接closed，而是处于closing的状态
        if (this._readyState === WebSocketState.CLOSING) {
          this.emit('closing')
        }
      }
    }
    await new Promise((resolve) => setTimeout(resolve, 2000))
    this._heathCheckTimer = requestAnimationFrame(this._healthCheck)
  }

  /**
   * 清理超时计时器
   */
  private _clearConnectTimeoutIfNeed = () => {
    if (this._connectTimeout) {
      clearTimeout(this._connectTimeout)
      this._connectTimeout = undefined
    }
  }

  /**
   * 清理健康检查
   */
  private _clearHeathCheckIfNeed = () => {
    if (this._heathCheckTimer) {
      cancelAnimationFrame(this._heathCheckTimer)
      this._heathCheckTimer = undefined
    }
  }

  /**
   * 开启健康检查
   */
  private _startHealthCheck = () => {
    this._clearHeathCheckIfNeed()
    this._heathCheckTimer = requestAnimationFrame(this._healthCheck)
  }

  /**
   * 停止健康检查
   */
  private _stopHealthCheck = () => {
    this._clearHeathCheckIfNeed()
  }

  /**
   * 清理连接中的请求
   */
  private _clearPendingReconnectIfNeeded = () => {
    if (this._pendingReconnect) {
      clearTimeout(this._pendingReconnect)
      this._pendingReconnect = undefined
    }
  }

  /**
   * 当网页连接上网络进行重连
   * @param event
   */
  private _online = (event: Event) => {
    if (this._reconnectWhenOnlineAgain) {
      this._clearPendingReconnectIfNeeded()
      this._reconnect(event as CloseEvent)
    }
  }

  /**
   * 当网页断开网络进行关闭
   */
  private _offline = () => {
    this._reconnectWhenOnlineAgain = true
    this._realWebSocket?.close(1000, 'offline')
  }

  /**
   * 解除网络监听事件
   */
  private _detachConnectivityEvents = () => {
    if (this._connectivityEventsAttached) {
      globalThis?.removeEventListener('online', this._online)
      globalThis?.removeEventListener('offline', this._offline)
      this._connectivityEventsAttached = false
    }
  }

  /**
   * 注册网络监听事件
   */
  private _attachConnectivityEvents = () => {
    if (!this._connectivityEventsAttached) {
      globalThis?.addEventListener('online', this._online)
      globalThis?.addEventListener('offline', this._offline)
      this._connectivityEventsAttached = true
    }
  }

  /**
   * 发送消息
   * @param data
   * @returns
   */
  send = (data: string | ArrayBuffer | Blob | ArrayBufferView): void | undefined => {
    if (this._realWebSocket?.readyState === this._realWebSocket?.OPEN) {
      this._realWebSocket?.send(data)
    }
  }

  /**
   * 关闭连接
   * @param code 代码
   * @param reason 原因
   * @returns
   */
  close = (code?: number | string, reason?: string): void | undefined => {
    if (typeof code !== 'number') {
      reason = code
      code = 1000
    }
    this._clearPendingReconnectIfNeeded()
    this._reconnectWhenOnlineAgain = false
    this._explicitlyClosed = true
    this._detachConnectivityEvents()

    return this._realWebSocket?.close(code, reason)
  }

  /**
   * 进行ws连接
   */
  open = (url?: string): void => {
    if (url) {
      this._url = url
    }
    if (
      this._realWebSocket?.readyState !== WebSocket.OPEN &&
      this._realWebSocket?.readyState !== WebSocket.CONNECTING
    ) {
      this._clearPendingReconnectIfNeeded()
      this._reconnectWhenOnlineAgain = false
      this._explicitlyClosed = false

      this._createWebSocket()
    }
  }

  /**
   * 创建ws连接
   */
  private _createWebSocket = () => {
    const newUrl = typeof this._url === 'string' ? this._url : this._url?.(this)
    if (!newUrl) return

    this._clearConnectTimeoutIfNeed()
    this._clearPendingReconnectIfNeeded()
    this._removeWebSocketEvent()
    this._realWebSocket = new WebSocket(newUrl, this._protocols || undefined)

    this._attempts++ // 简化自增操作
    this.emit(
      'connecting',
      new CustomEvent('connecting', {
        detail: { attempts: this._attempts, reconnects: this._reconnects },
      }),
    )

    const timeoutCallback = () => {
      this._connectTimeout = undefined
      this._detachConnectivityEvents()
      this.emit(
        'timeout',
        new CustomEvent('timeout', {
          detail: {
            attempts: this._attempts,
            reconnects: this._reconnects,
          },
        }),
      )
    }

    this._connectTimeout = setTimeout(timeoutCallback, this._options.timeout)

    this._dispatchWebSocketEvent()

    if (!this._options.ignoreConnectivityEvents) {
      this._attachConnectivityEvents()
    }
  }

  /**
   * 断开ws事件
   */
  private _removeWebSocketEvent = () => {
    this._realWebSocket?.removeEventListener('open', this._webSocketOpenHandler)
    this._realWebSocket?.removeEventListener('close', this._webSocketCloseHandler)
    this._realWebSocket?.removeEventListener('error', this._webSocketErrorHandler)
    this._realWebSocket?.removeEventListener('message', this._webSocketMessageHandler)
  }

  /**
   * 分发ws事件
   */
  private _dispatchWebSocketEvent = () => {
    this._realWebSocket?.addEventListener('open', this._webSocketOpenHandler)
    this._realWebSocket?.addEventListener('close', this._webSocketCloseHandler)
    this._realWebSocket?.addEventListener('error', this._webSocketErrorHandler)
    this._realWebSocket?.addEventListener('message', this._webSocketMessageHandler)
  }

  /**
   * ws连接事件
   * @returns
   */
  private _webSocketOpenHandler = (event: Event) => {
    if (this._connectTimeout) {
      clearTimeout(this._connectTimeout)
      this._connectTimeout = undefined
    }
    const reconnects = ++this._reconnects
    const attempts = this._attempts
    this._attempts = 0
    this._reconnectWhenOnlineAgain = false
    this._startHealthCheck()
    return this.emit('open', Object.assign(event, { detail: { reconnects, attempts } }))
  }

  /**
   * ws 关闭事件
   * @param event
   * @returns
   */
  private _webSocketCloseHandler = (event: CloseEvent) => {
    this._reconnect(event)
    this._stopHealthCheck()
    return this.emit('close', event)
  }

  /**
   * ws 异常
   * @param event
   * @returns
   */
  private _webSocketErrorHandler = (event: Event) => {
    return this.emit('error', new CustomEvent('error', { detail: { error: event } }))
  }

  /**
   * ws消息
   * @param event
   * @returns
   */
  private _webSocketMessageHandler = (event: MessageEvent) => {
    return this.emit('message', event)
  }

  /**
   * 进行重连
   * @param event
   * @returns
   */
  private _reconnect(event: CloseEvent) {
    // 如果关闭代码是1000且不需要重连，或者连接是显式关闭的，则不重连
    const shouldNotReconnect = (!this._options.should1000Reconnect && event.code === 1000) || this._explicitlyClosed
    if (shouldNotReconnect) {
      this._attempts = 0
      return
    }

    // 如果网络断开，设置标志以便在网络恢复时重连
    const isOffline = this._connectivityEventsAttached && globalThis?.navigator?.onLine === false
    if (isOffline) {
      this._reconnectWhenOnlineAgain = true
      return
    }

    // 获取重连延迟时间
    const delay = this._options.shouldReconnect?.(event, this)
    if (typeof delay === 'number') {
      logger.debug(`RobustWebSocket: Reconnecting in ${delay}ms...`)
      this._pendingReconnect = setTimeout(this._createWebSocket, delay)
    } else {
      logger.debug('RobustWebSocket: No reconnection will be attempted.')
    }
  }
}
